import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Input, Select } from "../../components/common/Primitives";
import { FieldWithHelp } from "../../components/common/FormPrimitives";
import { ExternalLink } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const sectors = ["Fintech", "CleanTech", "HealthTech", "SaaS", "Consumer", "MSME", "Manufacturing", "Enterprise SaaS", "AgriTech", "EdTech", "Logistics"];
const stages = ["Idea", "Pre-Seed", "Seed", "Pre-Series A", "Series A", "Series B", "Growth"];
const geos = ["Bengaluru", "Mumbai", "Delhi NCR", "Pune", "Hyderabad", "Chennai", "India-wide", "Global"];

// Structured option lists — nudges the investor toward a valid entry.
const EXPECTED_RETURN_OPTIONS = ["8-12% (income)", "12-18% (balanced)", "18-25% (growth)", "25-40% (aggressive)", "40%+ (venture)"];
const HORIZON_OPTIONS = ["< 1 year", "1-3 years", "3-5 years", "5-7 years", "7+ years"];
const DEAL_TYPE_OPTIONS = ["Lead", "Follow-on", "Co-invest"];
const TRACTION_OPTIONS = ["Pre-revenue", "Early revenue (< ₹1 Cr ARR)", "Growth (₹1-10 Cr ARR)", "Scale (₹10-50 Cr ARR)", "Mature (₹50 Cr+ ARR)"];

export default function InvestorPreferences() {
  const [form, setForm] = useState({
    preferred_sectors: [], preferred_industries: [], preferred_stages: [], preferred_geography: [],
    excluded_sectors: [], ticket_size_min: "", ticket_size_max: "",
    equity_debt_preference: "", risk_appetite: "", expected_return: "", investment_horizon: "",
    preferred_deal_type: "", preferred_founder_profile: "", preferred_traction_level: "",
  });

  useEffect(() => {
    api.get("/investor/me").then((r) => {
      const p = r.data?.preferences || {};
      setForm({
        preferred_sectors: p.preferred_sectors || [],
        preferred_industries: p.preferred_industries || [],
        preferred_stages: p.preferred_stages || [],
        preferred_geography: p.preferred_geography || [],
        excluded_sectors: p.excluded_sectors || [],
        ticket_size_min: p.ticket_size_min || "",
        ticket_size_max: p.ticket_size_max || "",
        equity_debt_preference: p.equity_debt_preference || "",
        risk_appetite: p.risk_appetite || "",
        expected_return: p.expected_return || "",
        investment_horizon: p.investment_horizon || "",
        preferred_deal_type: p.preferred_deal_type || "",
        preferred_founder_profile: p.preferred_founder_profile || "",
        preferred_traction_level: p.preferred_traction_level || "",
      });
    });
  }, []);

  const toggle = (key, val) => {
    const arr = form[key] || [];
    setForm({ ...form, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const isLinkedIn = (v) => !v || /^https?:\/\/(www\.)?linkedin\.com\//i.test(v);

  const save = async (e) => {
    e.preventDefault();
    if (!isLinkedIn(form.preferred_founder_profile)) {
      toast.error("Founder Profile must be a linkedin.com URL (or leave blank).");
      return;
    }
    try {
      const payload = {
        ...form,
        ticket_size_min: form.ticket_size_min ? Number(form.ticket_size_min) : null,
        ticket_size_max: form.ticket_size_max ? Number(form.ticket_size_max) : null,
      };
      await api.put("/investor/preferences", payload);
      toast.success("Preferences saved.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardShell title="Investment Preferences" subtitle="INVESTOR · MANDATE">
      <Toaster richColors position="top-right" />
      <form onSubmit={save} className="space-y-6 max-w-5xl" data-testid="prefs-form">
        <ChipsSection label="Preferred Sectors" values={sectors} selected={form.preferred_sectors} onToggle={(v) => toggle("preferred_sectors", v)} />
        <ChipsSection label="Preferred Stages" values={stages} selected={form.preferred_stages} onToggle={(v) => toggle("preferred_stages", v)} />
        <ChipsSection label="Preferred Geography" values={geos} selected={form.preferred_geography} onToggle={(v) => toggle("preferred_geography", v)} />
        <ChipsSection label="Excluded Sectors" values={sectors} selected={form.excluded_sectors} onToggle={(v) => toggle("excluded_sectors", v)} tone="danger" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Ticket Size Min (₹)" type="number" value={form.ticket_size_min} onChange={(e) => setForm({ ...form, ticket_size_min: e.target.value })} testId="prefs-min" />
          <Input label="Ticket Size Max (₹)" type="number" value={form.ticket_size_max} onChange={(e) => setForm({ ...form, ticket_size_max: e.target.value })} testId="prefs-max" />
          <Select label="Equity / Debt Preference" value={form.equity_debt_preference} onChange={(e) => setForm({ ...form, equity_debt_preference: e.target.value })} testId="prefs-eqdebt">
            <option value="">Any</option>
            <option value="Equity">Equity</option>
            <option value="Debt">Debt</option>
            <option value="Hybrid">Hybrid</option>
            <option value="Convertible">Convertible</option>
          </Select>
          <Select label="Risk Appetite" value={form.risk_appetite} onChange={(e) => setForm({ ...form, risk_appetite: e.target.value })} testId="prefs-risk">
            <option value="">Select</option>
            <option>Conservative</option><option>Balanced</option><option>Aggressive</option>
          </Select>

          <FieldWithHelp
            label="Expected Return"
            tooltip="Target internal rate of return (IRR) on each investment."
            nudge="Pick a band — startups pitch you accordingly."
            testId="prefs-return"
          >
            <select
              value={form.expected_return}
              onChange={(e) => setForm({ ...form, expected_return: e.target.value })}
              className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm rounded-md focus:border-gold outline-none"
              data-testid="prefs-return-input"
            >
              <option value="">Select target return</option>
              {EXPECTED_RETURN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </FieldWithHelp>

          <FieldWithHelp
            label="Investment Horizon"
            tooltip="How long you typically hold an investment before exit."
            nudge="Shorter horizons prefer revenue-stage deals; longer horizons unlock early-stage."
            testId="prefs-horizon"
          >
            <select
              value={form.investment_horizon}
              onChange={(e) => setForm({ ...form, investment_horizon: e.target.value })}
              className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm rounded-md focus:border-gold outline-none"
              data-testid="prefs-horizon-input"
            >
              <option value="">Select horizon</option>
              {HORIZON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </FieldWithHelp>

          <FieldWithHelp
            label="Preferred Deal Type"
            tooltip="How you like to participate in a round. Lead sets terms; Follow-on joins a lead; Co-invest is alongside institutional leads."
            nudge="Leads see term-sheet control; Follow-ons ride established checks; Co-invest for lower friction."
            testId="prefs-deal"
          >
            <select
              value={form.preferred_deal_type}
              onChange={(e) => setForm({ ...form, preferred_deal_type: e.target.value })}
              className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm rounded-md focus:border-gold outline-none"
              data-testid="prefs-deal-input"
            >
              <option value="">Select style</option>
              {DEAL_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </FieldWithHelp>

          <FieldWithHelp
            label="Founder Profile (LinkedIn URL)"
            tooltip="Paste the LinkedIn URL of a founder archetype you look for — helps us surface similar founders."
            nudge="Full URL only — e.g. https://linkedin.com/in/founder-name"
            testId="prefs-founder"
          >
            <div className="relative">
              <input
                type="text"
                inputMode="url"
                value={form.preferred_founder_profile}
                onChange={(e) => setForm({ ...form, preferred_founder_profile: e.target.value })}
                placeholder="https://linkedin.com/in/…"
                className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 pr-9 text-sm rounded-md focus:border-gold outline-none"
                data-testid="prefs-founder-input"
              />
              {form.preferred_founder_profile && isLinkedIn(form.preferred_founder_profile) && (
                <a
                  href={form.preferred_founder_profile}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-600 hover:text-emerald-700"
                  title="Open profile"
                >
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
            {form.preferred_founder_profile && !isLinkedIn(form.preferred_founder_profile) && (
              <div className="text-[11px] text-rose-600 mt-1" data-testid="prefs-founder-error">
                Must start with https://linkedin.com/ …
              </div>
            )}
          </FieldWithHelp>

          <FieldWithHelp
            label="Traction Level"
            tooltip="Where the startup's revenue / usage should be at the time you invest."
            nudge="Aligns you with founders reporting matching ARR band."
            testId="prefs-traction"
          >
            <select
              value={form.preferred_traction_level}
              onChange={(e) => setForm({ ...form, preferred_traction_level: e.target.value })}
              className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm rounded-md focus:border-gold outline-none"
              data-testid="prefs-traction-input"
            >
              <option value="">Select level</option>
              {TRACTION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </FieldWithHelp>
        </div>

        <Button type="submit" data-testid="prefs-save-btn">Save Preferences</Button>
      </form>
    </DashboardShell>
  );
}

function ChipsSection({ label, values, selected, onToggle, tone = "gold" }) {
  return (
    <div>
      <div className="overline mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        {values.map((v) => {
          const on = (selected || []).includes(v);
          return (
            <button
              type="button"
              key={v}
              onClick={() => onToggle(v)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
                on
                  ? tone === "danger"
                    ? "border-rose-400/50 text-rose-300 bg-rose-500/10"
                    : "border-gold text-gold bg-gold/10"
                  : "border-white/15 text-ink-muted hover:border-white/30"
              }`}
              data-testid={`chip-${label.toLowerCase().replace(/\s+/g, '-')}-${v}`}
            >{v}</button>
          );
        })}
      </div>
    </div>
  );
}
